import type { UseFormReturn } from 'react-hook-form';

import { useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { IconName } from '~/components';
import type { User } from '~/types/user';

import { useUploadCommunityProfilePicture } from '~/api/community';
import { communityRegions } from '~/api/community/constants';
import { useUser } from '~/api/user';
import { Button, Dialog, ExternalLink, RichTextEditor, Text } from '~/components';
import { Combobox, Field, Input, Select } from '~/components/Form';
import { Activity, maxImageUploadSize } from '~/constants/constants';
import { EMAIL } from '~/constants/regex';
import { CommunityProfileDialog } from '~/features/community';
import { useIntl } from '~/hooks/useIntl';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { EditUserCommunityProfileFormType } from './types';

import { FormSwitchField } from '../../components/FormSwitchField';
import { ImageUploadField } from '../../components/ImageUploadField';
import { actionsStyle, bannedStyle, fieldsStyle, layoutStyle, linksStyle } from './CommunitySettings.css';

const linkFields = ['website', 'linkedIn', 'instagram', 'facebook', 'behance'] as const;

const linkFieldIcons: Record<(typeof linkFields)[number], IconName> = {
  website: 'Link',
  linkedIn: 'LinkedIn',
  instagram: 'Instagram',
  facebook: 'Facebook',
  behance: 'Behance',
};

type Props = {
  form: UseFormReturn<EditUserCommunityProfileFormType>;
  isEnabled: boolean;
};

export const CommunitySettings = ({ form, isEnabled }: Props) => {
  const { data: user } = useUser();
  const uploadMutation = useUploadCommunityProfilePicture(user.communityProfile.id);

  const { control, getValues, setValue } = form;

  const { t } = useTranslation(['common', 'community', 'settings', 'validation']);
  const { compareForSort } = useIntl();

  const [pendingPicture, setPendingPicture] = useState<'delete' | File | null>(null);

  const onProfilePictureChange = (profilePicture: File | null) => {
    if (profilePicture && profilePicture.size > maxImageUploadSize) {
      return toast.danger({ description: t('settings:alerts.community.errorProfilePictureTooLarge') });
    }

    const formData = new FormData();
    if (profilePicture) formData.append('profilePicture', profilePicture);

    setPendingPicture(profilePicture ?? 'delete');

    uploadMutation.mutate(formData, {
      onSuccess: () =>
        toast.success({
          description: profilePicture
            ? t('settings:alerts.community.successProfilePictureUploaded')
            : t('settings:alerts.community.successProfilePictureDeleted'),
        }),
      onError: () =>
        toast.danger({
          description: profilePicture
            ? t('settings:alerts.community.errorProfilePictureUploaded')
            : t('settings:alerts.community.errorProfilePictureDeleted'),
        }),
      onSettled: () => setPendingPicture(null),
    });
  };

  const brandItems = user.brands.map((brand) => ({ label: brand.companyName, value: brand.id }));

  const regionOptions = useMemo(
    () =>
      [...communityRegions]
        .map((region) => ({ value: region, label: t(`community:regions.${region}`) }))
        .sort(({ label: a }, { label: b }) => compareForSort(a, b)),
    [compareForSort, t],
  );
  const regionItems = Combobox.useItems(regionOptions, { getLabel: (option) => option.label, getValue: (option) => option.value });

  if (user.communityProfile.banned) {
    return (
      <Text className={bannedStyle}>
        <Trans components={{ a: <ExternalLink /> }} i18nKey="settings:profile.community.communityHidden" />
      </Text>
    );
  }

  if (!isEnabled) return null;

  return (
    <>
      <div className={layoutStyle}>
        <ImageUploadField
          deleteLabel={t('settings:imageUpload.profilePicture.delete')}
          isDeleting={pendingPicture === 'delete'}
          isUploading={pendingPicture instanceof File}
          label={t('settings:imageUpload.profilePicture.label')}
          onDelete={() => onProfilePictureChange(null)}
          onUpload={onProfilePictureChange}
          pendingFile={pendingPicture instanceof File ? pendingPicture : null}
          uploadLabel={t('settings:imageUpload.profilePicture.upload')}
          url={user.communityProfile.profilePictureUrl}
        />

        <div className={fieldsStyle}>
          <Controller
            control={control}
            name="email"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label>{t('settings:fields.communityEmail.label')}</Field.Label>

                <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              pattern: { value: EMAIL, message: t('validation:email.invalid') },
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            }}
          />

          <Controller
            control={control}
            name="openToWork"
            render={({ field: { onBlur, onChange, ref, value } }) => (
              <FormSwitchField checked={value} inputRef={ref} onBlur={onBlur} onCheckedChange={onChange}>
                {t('settings:fields.communityOpenToWork.label')}
              </FormSwitchField>
            )}
          />

          <Controller
            control={control}
            name="regions"
            render={({ field: { name, onChange, value } }) => (
              <Field.Root name={name}>
                <Field.Label>{t('settings:fields.communityRegion.label')}</Field.Label>

                <Combobox.Root items={regionItems} multiple onValueChange={onChange} value={value}>
                  <Combobox.ChipsButton
                    getLabel={(region: (typeof communityRegions)[number]) => t(`community:regions.${region}`)}
                    placeholder={t('settings:fields.communityRegion.placeholder')}
                  />

                  <Combobox.List>
                    {(item: (typeof regionOptions)[number]) => (
                      <Combobox.Item key={item.value} value={item.value}>
                        {item.label}
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Root>
              </Field.Root>
            )}
          />

          {user.brands.length > 1 && (
            <Controller
              control={control}
              name="brandId"
              render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid} name={name}>
                  <Field.Label>{t('settings:fields.brandId.label')}</Field.Label>

                  <Select.Root
                    items={brandItems}
                    onValueChange={(newValue) => {
                      const oldValue = value;
                      const expectedEmail = oldValue === 0 ? user.email : user.brands.find(({ id }) => id === oldValue)?.email;

                      if (getValues('email') === expectedEmail) {
                        const email = newValue === 0 ? user.email : (user.brands.find(({ id }) => id === newValue)?.email ?? '');
                        setValue('email', email, { shouldDirty: true });
                      }

                      onChange(newValue);
                    }}
                    value={value}
                  >
                    <Select.Button />

                    <Select.List>
                      {brandItems.map((item) => (
                        <Select.Item key={item.value} value={item.value}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Root>

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{ required: { value: true, message: t('validation:required') } }}
            />
          )}

          <Controller
            control={control}
            name="description"
            render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
              <Field.Root name={name}>
                <Field.Label optional>{t('settings:fields.communityDescription.label')}</Field.Label>

                <RichTextEditor isInvalid={invalid} mode="html" onChange={onChange} value={value} withLinkButton={false} />
              </Field.Root>
            )}
          />

          <div className={linksStyle}>
            {linkFields.map((linkField, index) => (
              <Controller
                control={control}
                key={linkField}
                name={`links.${linkField}`}
                render={({ field: { name, onBlur, onChange, ref, value } }) => (
                  <Field.Root name={name}>
                    {index === 0 && <Field.Label optional>{t('settings:fields.communityLinks.label')}</Field.Label>}

                    <Input
                      icon={linkFieldIcons[linkField]}
                      onBlur={onBlur}
                      onValueChange={onChange}
                      placeholder={t(`settings:fields.communityLinks.placeholders.${linkField}`)}
                      ref={ref}
                      value={value}
                    />
                  </Field.Root>
                )}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={actionsStyle}>
        <Dialog.Root>
          <Dialog.Trigger
            render={
              <Button emphasis="secondary" icon="Visibility">
                {t('community:header.preview')}
              </Button>
            }
          />

          <CommunityProfilePreview form={form} user={user} />
        </Dialog.Root>
      </div>
    </>
  );
};

const CommunityProfilePreview = ({ form, user }: Pick<Props, 'form'> & { user: User }) => {
  const values = useWatch({ control: form.control }) as EditUserCommunityProfileFormType;

  return (
    <CommunityProfileDialog
      communityProfile={{
        id: user.communityProfile.id,
        firstName: user.firstName,
        lastName: user.lastName,
        brand: user.brands.find((brand) => brand.id === values.brandId)?.companyName ?? null,
        openToWork: values.openToWork,
        email: values.email,
        description: values.description,
        regions: values.regions,
        profilePictureUrl: user.communityProfile.profilePictureUrl,
        activities: user.activities.filter(({ id }) => id !== Activity.other),
        links: values.links,
      }}
    />
  );
};
